import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * NALLI — Recently Viewed (PRD §4a), ported from theme 189670785323.
 *
 * The section is the Nalli Product Slider re-sourced, so the markup, CSS and
 * `_product-card` blocks are all the theme's own — this script only swaps in
 * the right products:
 *
 *   1. records the current PDP view
 *   2. fetches the SAME section rendered in search context (so Liquid builds
 *      real cards for the viewed product ids)
 *   3. reorders the cards by recency + past-purchase bias, drops the current
 *      product, trims to the configured count
 *   4. swaps them into the live section and re-runs the pagination installer
 *      (the v1.1.x original re-inited Swiper here; v2's native slideshow
 *      custom elements self-upgrade on insertion, so only the pagination bar
 *      needs a re-run)
 */

async function renderRail(root) {
  const sectionId = root.dataset.rvSectionId;
  const currentId = root.dataset.rvCurrentId || null;
  const minItems = parseInt(root.dataset.rvMinItems || '3', 10);
  const limit = parseInt(root.dataset.rvCount || '10', 10);
  const searchUrl = root.dataset.rvSearchUrl || '/search';

  // Record this PDP view first (PRD: last 15, 30 days, deduped, newest first).
  // scripts.liquid also records — addProduct dedupes, so this is idempotent.
  if (currentId) RecentlyViewed.addProduct(currentId);

  const ids = RecentlyViewed.getProducts().filter((id) => id !== currentId);

  // PRD: hide the widget entirely below the threshold.
  if (!sectionId || ids.length < minItems) return;

  const recencyRank = new Map(ids.map((id, i) => [id, i]));
  const biasTypes = new Set(
    (root.dataset.rvBiasTypes || '')
      .split(',')
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean)
  );

  const url = new URL(searchUrl, location.origin);
  url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
  url.searchParams.set('type', 'product');
  url.searchParams.set('section_id', sectionId);

  const response = await fetch(url);
  if (!response.ok) return;

  const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
  const fetched = doc.querySelector(`#nalli-rv-${CSS.escape(sectionId)}`) || doc.querySelector('[data-rv-section-id]');
  if (!fetched) return;

  const items = Array.from(fetched.querySelectorAll('.resource-list__item[data-product-id]')).filter((item) => {
    const id = item.dataset.productId;
    return id && id !== currentId && recencyRank.has(id);
  });

  if (items.length < minItems) return;

  items.sort((a, b) => {
    const biasA = biasTypes.has((a.dataset.productType || '').toLowerCase()) ? 0 : 1;
    const biasB = biasTypes.has((b.dataset.productType || '').toLowerCase()) ? 0 : 1;
    if (biasA !== biasB) return biasA - biasB;
    return (recencyRank.get(a.dataset.productId) ?? 0) - (recencyRank.get(b.dataset.productId) ?? 0);
  });

  // Slide unit = the carousel slide wrapping a card; grid items stand alone.
  // The slideshow component walks slides by DOM order (ref="slides[]"), so
  // reordering the elements before insertion is all it needs.
  const units = items.map((item) => item.closest('slideshow-slide') || item);
  const track = units[0].parentElement;
  track.replaceChildren(...units.slice(0, limit));

  /* NALLI-PERF 2026-08-19: force the rail's card images lazy before they ever reach the
     live DOM.

     card-gallery.liquid decides eager-vs-lazy with `if section.index == null or
     section.index < 5`, and in a Section Rendering API response (?section_id=) section.index
     is ALWAYS null -- so every card in this fetch came back loading="eager". Stock Horizon
     means that for a section rendered above the fold on first paint, but this rail is
     injected by JS long after load and sits at the bottom of the PDP, so eager is exactly
     backwards: measured 10 full-size product images fetched at once on every product page.

     Safe to do here because `fetched` belongs to a DOMParser document, which never loads
     resources -- the rewrite lands before the markup is serialised into the real DOM. */
  for (const img of fetched.querySelectorAll('img[loading="eager"]')) {
    img.setAttribute('loading', 'lazy');
    img.setAttribute('fetchpriority', 'low');
  }

  // Swap the theme's own markup in wholesale, then re-run the carousel
  // pagination installer (exposed by the section's inline script).
  root.innerHTML = fetched.innerHTML;
  root.hidden = false;

  if (typeof window.__nalliRvPaginationSetup === 'function') {
    window.__nalliRvPaginationSetup(root);
  }
}

function init() {
  for (const root of document.querySelectorAll('[data-rv-section-id]')) {
    if (root.dataset.rvDone === 'true') continue;
    root.dataset.rvDone = 'true';
    renderRail(root).catch((error) => console.error('Recently viewed error:', error));
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
